import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowLeft, FaBook, FaCalendarAlt, FaFileAlt, FaSpinner, FaClock, FaEye, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const StudentHomeworks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const [studentData, setStudentData] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStudentHomeworks = async () => {
      try {
        setLoading(true);
        
        let endpoint;
        if (userType === 'teacher') {
          endpoint = `${config.apiURL}/teacher/student/${id}/homeworks`;
        } else {
          throw new Error('Acesso não autorizado');
        }

        const response = await axios.get(endpoint);
        setStudentData(response.data.student);
        setHomeworks(response.data.homeworks || []);
      } catch (err) {
        console.error('Erro ao carregar lições do estudante:', err);
        setError('Erro ao carregar lições do estudante');
      } finally {
        setLoading(false);
      }
    };

    fetchStudentHomeworks();
  }, [id, userType]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getDueDateStatus = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: 'Atrasado', color: 'text-red-600', bgColor: 'bg-red-100' };
    } else if (diffDays === 0) {
      return { text: 'Hoje', color: 'text-orange-600', bgColor: 'bg-orange-100' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays} dias`, color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    } else {
      return { text: `${diffDays} dias`, color: 'text-green-600', bgColor: 'bg-green-100' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando lições...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FaArrowLeft className="mr-2" />
            Voltar
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Lições de {studentData?.name}
              </h1>
              <p className="mt-2 text-gray-600">
                Visualize todas as lições atribuídas a este estudante
              </p>
            </div>
          </div>
        </div>

        {/* Student Info Card */}
        {studentData && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-white font-medium text-lg">
                    {studentData.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{studentData.name}</h3>
                <p className="text-sm text-gray-500">Série: {studentData.grade}</p>
                <p className="text-sm text-gray-500">Data de nascimento: {formatDate(studentData.birth_date)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Homeworks List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Lições Atribuídas ({homeworks.length})
            </h3>
          </div>
          
          <div className="overflow-hidden">
            {homeworks.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {homeworks.map((homework) => {
                  const dueDateStatus = getDueDateStatus(homework.due_date);
                  return (
                    <li key={homework.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-lg font-medium text-gray-900">
                              {homework.title}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${dueDateStatus.bgColor} ${dueDateStatus.color}`}>
                                <FaClock className="mr-1 h-3 w-3" />
                                {dueDateStatus.text}
                              </span>
                            </div>
                          </div>
                          
                          <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                            {homework.description}
                          </p>
                          
                          <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                            <div className="flex items-center">
                              <FaFileAlt className="mr-1 h-4 w-4" />
                              {homework.subject}
                            </div>
                            <div className="flex items-center">
                              <FaCalendarAlt className="mr-1 h-4 w-4" />
                              Prazo: {formatDate(homework.due_date)}
                            </div>
                            <div className="flex items-center">
                              <FaClock className="mr-1 h-4 w-4" />
                              Criado: {formatDate(homework.created_at)}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="mt-4 flex items-center space-x-3">
                            {homework.homework_file && (
                              <a
                                href={homework.homework_file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <FaDownload className="mr-1 h-3 w-3" />
                                Baixar Lição
                              </a>
                            )}
                            
                            {homework.show_answer_key_to_student && homework.answer_key_file && (
                              <a
                                href={homework.answer_key_file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1 border border-green-300 shadow-sm text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                              >
                                <FaEye className="mr-1 h-3 w-3" />
                                Gabarito
                              </a>
                            )}
                            
                            <button
                              onClick={() => navigate(`/homework/${homework.id}/submissions`)}
                              className="inline-flex items-center px-3 py-1 border border-blue-300 shadow-sm text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                            >
                              <FaEye className="mr-1 h-3 w-3" />
                              Ver Submissões
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="px-6 py-12 text-center">
                <FaBook className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Nenhuma lição encontrada
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Este estudante ainda não possui lições atribuídas.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/create-homework')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FaBook className="mr-2 h-4 w-4" />
                    Criar Nova Lição
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentHomeworks; 